<template>
  <k-modal class="update-video-modal" v-if="video"
    :show="show"
    :closeOnClickAway="false"
    @close="$emit('close')">
    <template #header>
      <h3><i class="fas fa-edit" aria-hidden="true"></i>Edit Video - {{video.id}}</h3>
    </template>
    <template #body>
      <video-form :key="video.id"
        v-model="videoMeta"
        :disabled="updateInProgress"
        :tagOptions="moduleTemplates"
        :videoFileName="currentVideoFileName"
        :transcriptFileName="currentTranscriptFileName"
        videoDropfieldLabel="Update Video"
        transcriptDropfieldLabel="Update Transcript"
        @is-valid="e => formValid = e"
        @video-file-change="onVideoFileChange"
        @transcript-file-change="onTranscriptFileChange"
        ref="videoForm"
      ></video-form>
    </template>
    <template #footer>
      <button @click="$emit('close')" class="btn btn-outlined cancel-btn">Close</button>
      <button @click="updateVideo" class="btn btn-success send-btn">Update</button>
    </template>
  </k-modal>
</template>

<style>
.update-video-modal .modal-container {
  width: 80%;
}

.update-video-modal .modal-body {
  overflow: visible;
}

/* So the button doesn't grow to the same height as the dropdown container when open */
.update-video-modal .k-word-tags > form > button {
  height: 38px;
}

.update-video-modal .k-search-dropdown-adv-content {
  position: unset;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KModal from '@base-components/k-modal.vue';
import getOrNull from '@utils/get-or-null';
import VideoForm from './video-form.vue';

export default {
  components: {
    'k-modal': KModal,
    VideoForm,
  },
  mixins: [TimeMixin],
  props: {
    video: {
      type: Object,
    },
    moduleTemplates: {
      type: Array,
    },
  },
  data() {
    return {
      videoMeta: {
        title: this.video?.name || '',
        description: this.video?.description || '',
        tags: this.video?.tags || [],
        dateRecorded: this.video ? this.getDate(this.video.date_recorded) : null,
      },
      updateInProgress: false,
      formValid: false,
      videoFile: undefined,
      transcriptFile: undefined,
    };
  },
  watch: {
    video() {
      if (this.video) {
        this.videoMeta = {
          title: this.video.name || '',
          description: this.video.description || '',
          tags: this.video.tags || [],
          dateRecorded: this.getDate(this.video.date_recorded),
        };
      } else {
        this.videoMeta = {
          title: '',
          description: '',
          tags: [],
          dateRecorded: null,
        };
      }
      this.videoFile = undefined;
      this.transcriptFile = undefined;
    },
    updateInProgress() {
      if (this.updateInProgress) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
  },
  computed: {
    show() {
      return Boolean(this.video);
    },
    currentVideoFileName() {
      if (this.video?.resource_link) {
        return this.video.resource_link.split('/').pop();
      }
      return null;
    },
    currentTranscriptFileName() {
      if (this.video?.transcript_link) {
        return this.video.transcript_link.split('/').pop();
      }
      return null;
    },
    videoFileName() {
      if (this.videoFile) {
        return this.videoFile[0].name;
      }
      return this.currentVideoFileName;
    },
    transcriptFileName() {
      if (this.transcriptFile) {
        return this.transcriptFile[0].name;
      }
      return this.currentTranscriptFileName;
    },
  },
  methods: {
    onVideoFileChange(file) {
      this.$logger.info('Staged file for update', { file: file.name });
      this.videoFile = file;
    },
    onTranscriptFileChange(file) {
      this.$logger.info('Staged transcript file for update', { file: file.name });
      this.transcriptFile = file;
    },
    updateVideoMeta() {
      const updatePayload = {
        video_file_name: this.videoFileName,
        transcript_file_name: this.transcriptFileName,
        name: this.videoMeta.title,
        description: this.videoMeta.description,
        date_recorded: this.formatDate(this.videoMeta.dateRecorded),
        tags: this.videoMeta.tags,
      };
      this.$logger.info('Updating video metadata', {
        videoId: this.video.id,
        updatePayload,
      }, true);
      return this.$http.put(`/api/curriculum/admin/video/${this.video.id}`, updatePayload).then(() => {
        this.$logger.info('Successfully updated video metadata', {
          videoId: this.video.id,
          name: this.videoMeta.title,
          description: this.videoMeta.description,
          dateRecorded: this.formatDate(this.videoMeta.dateRecorded),
          tags: this.videoMeta.tags,
        });
      }).catch(err => {
        this.$logger.error('Error updating video', { videoId: this.video.id, updatePayload }, err);
        throw err;
      });
    },

    uploadVideoToUrl(videoId, url) {
      this.$logger.info('Uploading video file', {
        videoId,
        fileName: this.videoFileName,
        fileSize: this.videoFile[0].size,
        fileType: this.videoFile[0].type,
      }, true);
      return this.$http.put(url, this.videoFile[0]).then(() => {
        this.$logger.info('Video file uploaded successfully', {
          videoId,
          fileName: this.videoFileName,
          fileSize: this.videoFile[0].size,
        });
      }).catch(err => {
        this.$logger.error('Error uploading video with signed resumable upload URL', undefined, err);
        throw err;
      });
    },
    getUploadUrl(videoId) {
      const payload = {
        file_name: this.videoFileName,
        file_size: this.videoFile[0].size,
        content_type: this.videoFile[0].type,
      };
      this.$logger.info('Getting signed upload URL for video', {
        videoId,
        fileName: this.videoFileName,
        fileSize: this.videoFile[0].size,
        fileType: this.videoFile[0].type,
      }, true);
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/upload-video-url`, payload)
        .then(response => {
          this.$logger.info('Got signed resumable upload URL', { videoId });
          return response.data.upload_url;
        }).catch(err => {
          this.$logger.error('Error getting signed resumable upload URL', undefined, err);
          throw err;
        });
    },
    startProcessing(videoId) {
      this.$logger.info('Starting video processing', {
        videoId,
        fileName: this.videoFileName,
        fileSize: this.videoFile[0].size,
        fileType: this.videoFile[0].type,
      }, true);
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/processing/start`).then(() => {
        this.$logger.info('Successfully started video processing', {
          videoId,
          fileName: this.videoFileName,
        });
      }).catch(err => {
        this.$logger.error('Error starting video processing', { videoId, file: this.videoFile.name }, err);
        throw err;
      });
    },
    finaliseUpdate() {
      this.$ktoast.success('Video updated');
      this.clearFields();
      this.$emit('update');
      this.$emit('close');
    },
    async uploadTranscriptFile(videoId) {
      const file = this.transcriptFile[0];
      const formData = new FormData();
      formData.append('file', file);
      this.$logger.info('Uploading transcript file as FormData', { videoId, fileName: file.name }, true);
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/upload-transcript`, formData)
        .then(() => {
          this.$logger.info('Transcript file uploaded successfully as FormData', { videoId });
        }).catch(err => {
          this.$logger.error('Error uploading transcript as FormData', { videoId }, err);
          throw err;
        });
    },
    async updateVideo() {
      this.updateInProgress = true;
      try {
        if (this.videoFile) {
          this.$logger.info('Getting signed resumable upload URL');
          const url = await this.getUploadUrl(this.video.id);
          await this.uploadVideoToUrl(this.video.id, url);
        }
        if (this.transcriptFile) {
          await this.uploadTranscriptFile(this.video.id);
        }
        await this.updateVideoMeta();
        if (this.videoFile) {
          await this.startProcessing(this.video.id);
        }
        this.finaliseUpdate();
      } catch (err) {
        if (getOrNull('response.status', err) === 400) {
          this.$ktoast.showError(err);
        } else {
          this.$ktoast.showError(err, true);
        }
      }
      this.updateInProgress = false;
    },

    clearFields() {
      if (this.$refs.videoForm) {
        this.$refs.videoForm.reset();
      }
      this.videoFile = undefined;
      this.transcriptFile = undefined;
      this.videoMeta = {
        title: '',
        description: '',
        tags: [],
        dateRecorded: null,
      };
    },
  },
};
</script>
